import type { Issue } from '../types/issue';
import { isDone } from './status';

// JIRA-CSV exportiert Ticket-Links als eigene Spalten. Die Namen sind je nach
// JIRA-Version / Admin-Config unterschiedlich. Die gängigsten Muster:
//
//   • "Inward issue link (Blocks)"    → dieses Ticket wird geblockt von <Wert>
//   • "Outward issue link (Blocks)"   → dieses Ticket blockt <Wert>
//   • analog für: Cloners, Duplicate, Dependency, Relates, Implement, …
//
// Wichtig: JIRA benennt die Spalten nach dem *outward*-Namen des Link-Typs
// (z.B. Link-Typ "Blocks" hat inward="is blocked by") — die Semantik erschließt
// sich also aus (Direction × Typ).
//
// Bei mehreren Links pro Ticket exportiert JIRA mehrere gleichnamige Spalten
// (Dedup passiert in unserem csv.ts, indem gleichnamige Header zu Arrays werden).

const HEADER_RE = /^\s*(Inward|Outward)\s+issue\s+link\s*\(([^)]+)\)\s*$/i;

export type LinkDirection = 'inward' | 'outward';

export type LinkColumn = {
  header: string;
  direction: LinkDirection;
  typeName: string;   // z.B. "Blocks", "Duplicate", "Depends"
};

// Semantische Klassifikation eines Link-Typs pro Richtung.
//   blocker      = das andere Ticket blockiert MICH  (kritisch)
//   blocking     = ICH blockiere das andere Ticket
//   related      = neutral (Cloners, Duplicate, Relates)
export type LinkClass = 'blocker' | 'blocking' | 'related';

export function classifyLink(direction: LinkDirection, typeName: string): LinkClass {
  const t = typeName.toLowerCase().trim();
  // "Blocks" ist der Standard-Blocker-Typ.
  //   inward  = ich werde geblockt          → blocker
  //   outward = ich blocke                   → blocking
  if (t.startsWith('block')) {
    return direction === 'inward' ? 'blocker' : 'blocking';
  }
  // "Depends" / "Dependency" (Atlassian Portfolio, Jira Advanced Roadmaps):
  //   outward = "depends on"                 → blocker (ich hänge ab)
  //   inward  = "is depended on by"          → blocking
  if (t.startsWith('depend')) {
    return direction === 'outward' ? 'blocker' : 'blocking';
  }
  // "Implement" (rare)
  if (t.startsWith('implement')) {
    return direction === 'outward' ? 'blocker' : 'blocking';
  }
  // "Cause" / "Caused by"
  if (t.startsWith('caus')) {
    return direction === 'inward' ? 'blocker' : 'blocking';
  }
  // Rest (Relates, Cloners, Duplicate, Test, …) = neutral
  return 'related';
}

// Menschenlesbares Label pro Kombi. Standard-JIRA-Namen.
export function linkLabel(direction: LinkDirection, typeName: string): string {
  const t = typeName.toLowerCase().trim();
  if (t.startsWith('block')) return direction === 'inward' ? 'is blocked by' : 'blocks';
  if (t.startsWith('depend')) return direction === 'outward' ? 'depends on' : 'is depended on by';
  if (t.startsWith('duplicat')) return direction === 'inward' ? 'is duplicated by' : 'duplicates';
  if (t.startsWith('clone')) return direction === 'inward' ? 'is cloned by' : 'clones';
  if (t.startsWith('caus')) return direction === 'inward' ? 'is caused by' : 'causes';
  if (t.startsWith('relate')) return 'relates to';
  if (t.startsWith('implement')) return direction === 'outward' ? 'implements' : 'is implemented by';
  return `${direction} ${typeName}`.toLowerCase();
}

// Findet alle Link-Spalten in den vorhandenen Rows. Läuft über eine
// Stichprobe (bis zu N Rows), damit auch spärlich gefüllte Link-Spalten
// erkannt werden.
export function findLinkColumns(rows: Issue[], sampleSize = 2000): LinkColumn[] {
  const found = new Map<string, LinkColumn>();
  const limit = Math.min(rows.length, sampleSize);
  for (let i = 0; i < limit; i++) {
    for (const key of Object.keys(rows[i])) {
      if (found.has(key)) continue;
      const m = key.match(HEADER_RE);
      if (!m) continue;
      found.set(key, {
        header: key,
        direction: m[1].toLowerCase() === 'inward' ? 'inward' : 'outward',
        typeName: m[2].trim(),
      });
    }
  }
  return Array.from(found.values()).sort((a, b) => a.header.localeCompare(b.header));
}

// Ein aufgelöster Link — mit Ziel-Ticket-Daten falls im Snapshot vorhanden.
export type ResolvedLink = {
  sourceKey: string;
  sourceTeam: string;
  sourceStatus: string;
  sourceType: string;
  targetKey: string;
  // Ziel-Team: entweder echter Team-Name aus dem Snapshot ODER Fallback auf
  // Projekt-Prefix mit „@"-Marker (z.B. „@SDO"), wenn Ziel-Ticket fehlt.
  targetTeam: string;
  targetStatus: string | null;
  direction: LinkDirection;
  typeName: string;
  cls: LinkClass;
};

// Ist der Ziel-Team-String ein Projekt-Prefix-Fallback (fehlendes Ziel)?
export function isProjectFallback(targetTeam: string): boolean {
  return targetTeam.startsWith('@');
}

// Extrahiert für ein Ticket alle Link-Zellen als Ziel-Keys.
function extractLinkKeys(row: Issue, header: string): string[] {
  const v = (row as Record<string, unknown>)[header];
  if (v == null || v === '') return [];
  const raw = Array.isArray(v) ? v : [v];
  const out: string[] = [];
  for (const item of raw) {
    if (item == null || item === '') continue;
    // Manche JIRA-Exports komma-separieren Mehrfach-Links in einer Zelle.
    const parts = String(item).split(/[,;]/).map(s => s.trim()).filter(Boolean);
    for (const p of parts) out.push(p);
  }
  return out;
}

export type BuildLinksOpts = {
  cols: LinkColumn[];
  onlyOpenSources?: boolean;   // nur Source-Tickets mit Status ∉ DONE_STATUSES
};

// Extrahiert den JIRA-Projekt-Prefix aus einem Issue-Key ("CAL-1234" → "CAL").
export function projectPrefixOf(key: string): string {
  const dash = key.indexOf('-');
  return dash > 0 ? key.slice(0, dash) : '';
}

export function buildResolvedLinks(rows: Issue[], opts: BuildLinksOpts): ResolvedLink[] {
  const byKey = new Map<string, Issue>();
  for (const r of rows) {
    const k = r['Issue key'];
    if (typeof k === 'string' && k) byKey.set(k, r);
  }
  const out: ResolvedLink[] = [];
  for (const r of rows) {
    const sourceStatus = (r['Status'] ?? '').trim();
    if (opts.onlyOpenSources && isDone(sourceStatus)) continue;
    const sourceKey = r['Issue key'];
    if (typeof sourceKey !== 'string' || !sourceKey) continue;
    const sourceTeam = (r['Team Name'] ?? '') as string;
    const sourceType = (r['Issue Type'] ?? '') as string;
    for (const c of opts.cols) {
      const keys = extractLinkKeys(r, c.header);
      if (!keys.length) continue;
      const cls = classifyLink(c.direction, c.typeName);
      for (const targetKey of keys) {
        const t = byKey.get(targetKey);
        // Fallback: wenn das Ziel-Ticket nicht im Snapshot ist, nehmen wir
        // den JIRA-Projekt-Prefix als Bucket. Mit „@"-Präfix, damit klar
        // wird: das ist ein Projekt, kein Team.
        const targetTeamFromRow = t ? ((t['Team Name'] ?? '') as string) : '';
        const targetTeam = targetTeamFromRow
          ? targetTeamFromRow
          : `@${projectPrefixOf(targetKey) || '?'}`;
        out.push({
          sourceKey,
          sourceTeam,
          sourceStatus,
          sourceType,
          targetKey,
          targetTeam,
          targetStatus: t ? ((t['Status'] ?? '') as string || null) : null,
          direction: c.direction,
          typeName: c.typeName,
          cls,
        });
      }
    }
  }
  return out;
}

// Aggregation: pro (Source-Team, Target-Team, Klasse) die Anzahl unique
// Source-Tickets. Ein Source-Ticket mit 3 Blocker-Links auf 3 Ziel-Teams
// zählt in jedem der drei Ziel-Team-Buckets einmal.
export type TeamMatrixCell = {
  sourceTeam: string;
  targetTeam: string;
  cls: LinkClass;
  uniqueSourceTickets: number;
  linkCount: number;
};

export function aggregateByTeam(links: ResolvedLink[]): TeamMatrixCell[] {
  const bucket = new Map<string, { keys: Set<string>; linkCount: number }>();
  const keyOf = (sTeam: string, tTeam: string, cls: LinkClass) =>
    `${sTeam}||${tTeam}||${cls}`;
  for (const l of links) {
    const k = keyOf(l.sourceTeam || '(kein Team)', l.targetTeam, l.cls);
    let e = bucket.get(k);
    if (!e) { e = { keys: new Set(), linkCount: 0 }; bucket.set(k, e); }
    e.keys.add(l.sourceKey);
    e.linkCount++;
  }
  const out: TeamMatrixCell[] = [];
  for (const [k, e] of bucket.entries()) {
    const [sourceTeam, targetTeam, cls] = k.split('||') as [string, string, LinkClass];
    out.push({
      sourceTeam,
      targetTeam,
      cls,
      uniqueSourceTickets: e.keys.size,
      linkCount: e.linkCount,
    });
  }
  return out;
}

// Alias-Mapping: der User definiert semantische Ziel-Teams, denen mehrere
// Projekt-Prefixes zugeordnet werden. Beispiel:
//   { "Tools": ["@SDO", "@DEVO"], "Features": ["@BU"] }
// Die Alias-Definition lebt in localStorage — der User pflegt sie per Widget.
export type AliasMap = Record<string, string[]>;

export function loadAliases(): AliasMap {
  try {
    const raw = localStorage.getItem('schuecal.depsAliases');
    if (raw) {
      const p = JSON.parse(raw);
      if (p && typeof p === 'object') return p as AliasMap;
    }
  } catch { /* noop */ }
  return {};
}

export function saveAliases(a: AliasMap): void {
  localStorage.setItem('schuecal.depsAliases', JSON.stringify(a));
}

// Nutzt Alias-Mapping, um Prefix-Fallback-Buckets in semantische Namen
// umzubenennen. Ein Link zum Ziel-Team "@SDO" wird — bei Alias
// {"Tools": ["@SDO"]} — zu Ziel-Team "Tools" zusammengelegt.
export function applyAliases(links: ResolvedLink[], aliases: AliasMap): ResolvedLink[] {
  if (!Object.keys(aliases).length) return links;
  const rev = new Map<string, string>();
  for (const [alias, prefixes] of Object.entries(aliases)) {
    for (const p of prefixes) rev.set(p, alias);
  }
  return links.map(l => {
    const aliased = rev.get(l.targetTeam);
    return aliased ? { ...l, targetTeam: aliased } : l;
  });
}
